import React from "react";

import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import {
  CheckBoxBase,
  Input,
  IntegerPriceInput,
  OptionTogglerField,
} from "components/Form/FormikControls";
import { Row } from "components/Layout";
import { SectionDivider } from "components/ReviewCard";

import { ForSheep } from "containers/ForSpecies";
import { ForHooks } from "containers/ForSaleType";
import { useToggle } from "hooks";
import { BILLING_REFERENCE_TOOLTIP } from "constants/consignments";
import { SaleType } from "constants/sale";
import { withNamespace } from "components/Form/FormikControls/lib";

import {
  MobileTransactionalCreateSaleFormValues,
  defaultSaleLot,
  defaultSkinLot,
} from "components/MobileTransactionalModal/lib/types";
import { FormRow } from "components/MobileTransactionalModal/Tabs/FormRow";
import {
  MultiSaleLotsForm,
  removeFormikSaleLotAtIndex,
} from "components/MultiSaleLotsForm";
import { MultiSkinsForm } from "components/MultiSkinsLotsForm";
import { PricingTypes } from "constants/pricingTypes";

interface WhatTabProps {
  deploymentId: number | null;
  speciesId: number | null;
  consignmentNamespace: string;
  skinsLotsNamespace: string;
  saleType?: SaleType | null;
}

export const WhatTab = ({
  deploymentId,
  speciesId,
  consignmentNamespace,
  skinsLotsNamespace,
  saleType: saleTypeProp,
}: WhatTabProps) => {
  const { values, setFieldValue } =
    useFormikContext<MobileTransactionalCreateSaleFormValues>();

  const saleType = values.sale?.sale_type ?? saleTypeProp ?? null;

  const carrierCharge = values.consignment?.carrierCharge;
  const isCarrierSet = !!carrierCharge?.carrier;
  const showCarrierSection = !!(
    carrierCharge?.carrier ||
    carrierCharge?.deliveryDate ||
    carrierCharge?.notes
  );

  const skinsLotsHaveQuantity =
    values.skinsLots?.some(skinLot => !!skinLot.quantity) ?? false;

  const [includeSkins, toggleIncludeSkins] = useToggle(skinsLotsHaveQuantity);

  const handleToggleSkins = () => {
    if (includeSkins) {
      setFieldValue(skinsLotsNamespace, [defaultSkinLot]);
      setFieldValue(
        withNamespace(consignmentNamespace, "billing_reference"),
        "",
      );
    }
    toggleIncludeSkins();
  };

  const { saleLots = [] } = values;

  const setPricingType = (pricingTypeId: number | null) => {
    setFieldValue(
      "saleLots",
      saleLots.map(saleLot => ({
        ...saleLot,
        pricing_type_id: pricingTypeId,
      })),
    );
  };

  const handleClickDelete = (_saleLot: SaleLot, index: number) => {
    removeFormikSaleLotAtIndex(index, saleLots, defaultSaleLot, setFieldValue);
  };

  const handleSkinsLotDelete = (_skinsLot: unknown, index: number) => {
    const next = (values.skinsLots ?? []).filter((_, i) => i !== index);
    setFieldValue(
      skinsLotsNamespace,
      next.length === 0 ? [defaultSkinLot] : next,
    );
  };

  return (
    <>
      <Grid container item xs={12}>
        <FormRow>
          <Grid container item xs={12} spacing={2}>
            <Grid xs={12} item>
              <OptionTogglerField
                name="saleLots[0].pricing_type_id"
                options={PricingTypes.all()}
                onChangeExtra={setPricingType}
              />
            </Grid>
            <MultiSaleLotsForm
              addLineText="Add Lot"
              handleClickDelete={handleClickDelete}
              showQuickCode={false}
              deploymentId={deploymentId}
              selectedSpeciesId={speciesId}
              selectedSaleType={saleType}
              showCalculationSummary
            />
            <ForSheep selectedSpeciesId={speciesId}>
              <FormRow>
                <ForHooks selectedSaleType={saleType}>
                  <Grid item xs={12}>
                    <Row justifyEnd>
                      <CheckBoxBase
                        label="Include Skins"
                        onChange={handleToggleSkins}
                        value={includeSkins}
                      />
                    </Row>
                  </Grid>
                </ForHooks>
              </FormRow>
            </ForSheep>
            {includeSkins && (
              <MultiSkinsForm
                handleClickDelete={handleSkinsLotDelete}
                addLineText="Add Skins"
                speciesId={speciesId}
              />
            )}
            {saleType === SaleType.OVER_HOOKS && (
              <Grid item xs={12}>
                <Input
                  label="External References/Kill Id"
                  name={withNamespace(
                    consignmentNamespace,
                    "billing_reference",
                  )}
                  maxLength={255}
                  tooltip={BILLING_REFERENCE_TOOLTIP}
                />
              </Grid>
            )}
            {showCarrierSection && (
              <>
                <SectionDivider>Carrier</SectionDivider>
                <Grid item xs={12}>
                  <Input
                    label="Invoice Number"
                    name={withNamespace(
                      consignmentNamespace,
                      "carrierCharge.invoiceNumber",
                    )}
                    required={isCarrierSet}
                  />
                </Grid>
                <Grid item xs={12}>
                  <IntegerPriceInput
                    label="Invoice Amount"
                    name={withNamespace(
                      consignmentNamespace,
                      "carrierCharge.invoiceAmount",
                    )}
                    min={0}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </FormRow>
      </Grid>
    </>
  );
};
